import { gql } from '@apollo/client'

export const GET_CONTENT_BY_ID = gql`
  query ContentTypeRichText($id: String!) {
    contentTypeRichText: contentTypeRichText(id: $id) {
      name
      fieldName
      uiComponent
      textContent {
        json
        links {
          entries {
            inline {
              ... on StyledText {
                sys {
                  id
                }
                plainText
                styledText
                variation
                identifier
              }
              ... on VideoModal {
                sys {
                  id
                }
                altText
                title
                source
                autoplay
                buttonCta
                videoPlayer {
                  fieldName
                  uiComponent
                  source
                  thumbnail {
                    url
                    fileName
                    height
                    width
                  }
                  altText
                  autoplay
                }
              }
            }
          }
          assets {
            hyperlink {
              sys {
                id
              }
              url
              title
              description
              contentType
              fileName
            }
          }
        }
      }
    }
  }
`

export const GET_GLOBAL_VARIABLES = gql`
  query fetchGlobalVariables {
    jsonFieldCollection(where: { name: "Frontend Global Variables" }) {
      items {
        jsonContent
      }
    }
  }
`

export const GET_ALL_GLOSSARY_TERMS = gql`
  query ($filter: GlossaryTermContentFilter!) {
    glossaryTermContentCollection(order: name_ASC, where: $filter, limit: 1000) {
      items {
        letter
        slug
        listTitle
        title
        summary
        sys {
          publishedAt
        }
      }
      total
    }
  }
`

// `block`/`inline` on every rich text field's `links.entries` resolve to the shared `Entry`
// interface, so one fragment covers pageSummary/mainBody/additionalInfo instead of repeating
// this inline 6x — Contentful's GraphQL API caps query documents at 8192 bytes.
const RICH_TEXT_EMBEDS_FRAGMENT = `
  fragment RichTextEmbeds on Entry {
    ... on StyledText {
      sys {
        id
      }
      plainText
      styledText
      variation
      identifier
    }
    ... on VideoModal {
      sys {
        id
      }
      altText
      title
      source
      autoplay
      buttonCta
    }
    ... on ContentTypeRichText {
      sys {
        id
      }
      uiComponent
      name
      fieldName
      textContent {
        json
      }
    }
  }
`

const RICH_TEXT_FIELD = `
  json
  links {
    entries {
      block {
        ...RichTextEmbeds
      }
      inline {
        ...RichTextEmbeds
      }
    }
    assets {
      block {
        sys {
          id
        }
        url
        title
        description
        contentType
        fileName
        width
        height
      }
    }
  }
`

// `faq` (Faqs2) holds real question/answer entries.
const FAQ_FIELD = `
  sys {
    id
  }
  fieldName
  name
  faqSetsCollection {
    items {
      sys {
        id
      }
      fieldName
      question {
        json
      }
      answer {
        json
      }
    }
  }
`

// `externalFaq` (WwwExternalFaqs, FDR only) holds cross-space links resolved later via
// resolveExternalEntry — same shape as GlossaryTermContentExternalRelatedArticlesItem.
const EXTERNAL_FAQ_FIELD = `
  sys {
    id
  }
  fieldName
  name
  faqSets {
    items {
      sys {
        urn
        linkType
      }
    }
  }
`

export const GET_GLOSSARY_TERM_BY_SLUG = gql`
  ${RICH_TEXT_EMBEDS_FRAGMENT}
  query GetGlossaryTermBySlug($filter: GlossaryTermContentFilter!) {
    glossaryTermContentCollection(where: $filter, limit: 1) {
      items {
        sys {
          id
        }
        slug
        title
        letter
        summary
        metaDescription
        pageMetaTitle
        associatedProduct
        canonicalLinkOverride
        pageSummary {
          ${RICH_TEXT_FIELD}
        }
        mainBody {
          ${RICH_TEXT_FIELD}
        }
        additionalInfo {
          ${RICH_TEXT_FIELD}
        }
        ctaCard {
          eyebrow
          cardTitleText {
            json
          }
          cardText {
            json
          }
          cardImage {
            url
            fileName
            height
            width
            title
            description
          }
          cardLink {
            ... on LInk {
              fieldName
              linkText {
                json
              }
              linkHref
              event
            }
          }
          linkAndroid {
            ... on LInk {
              linkHref
            }
          }
          linkApple {
            ... on LInk {
              linkHref
            }
          }
          imageApple {
            url
            fileName
            height
            width
          }
          imageAndroid {
            url
            fileName
            height
            width
          }
        }
        faq {
          ${FAQ_FIELD}
        }
        externalFaq {
          ${EXTERNAL_FAQ_FIELD}
        }
        relatedArticlesCollection {
          items {
            sys {
              id
            }
            slug
            title
            hook
            metaDescription
            contentType
            primaryCategory
            author {
              authorName
              authorByline
              slug
              authorPhoto {
                url
                fileName
              }
            }
            reviewedBy {
              authorName
              authorByline
              slug
              authorPhoto {
                url
                fileName
              }
            }
            mainImage {
              __typename
              ... on MediaField {
                fieldName
                mediaContent {
                  url
                  height
                  width
                }
              }
              ... on VideoPlayer {
                fieldName
                uiComponent
                source
                thumbnail {
                  url
                  fileName
                  height
                  width
                }
                altText
                autoplay
              }
            }
          }
        }
        associatedTermCollection {
          items {
            sys {
              id
            }
            slug
            title
            letter
          }
        }
        externalRelatedArticles {
          items {
            sys {
              urn
              linkType
            }
          }
        }
        pageMetaDataCollection {
          items {
            ... on PageMetaData {
              name
              content
            }
          }
        }
        secondaryDisclosuresCollection {
          items {
            ... on ContentTypeRichText {
              textContent {
                json
              }
            }
          }
        }
      }
      total
    }
  }
`

export const GET_ALL_TRUST_MARKERS = gql`
  query ($filter: TrustMarkerFilter!) {
    trustMarkerCollection(order: title_ASC, where: $filter) {
      items {
        network
        rating
        totalReview
        letterGrade
        lastTimeVerified
        sys {
          id
        }
      }
      total
    }
  }
`

export const GET_ALL_ADVERTORIAL_PAGES = gql`
  query ($filter: AdvertorialFilter!) {
    advertorialCollection(order: title_ASC, where: $filter) {
      items {
        slug
        title
        sys {
          publishedAt
        }
      }
      total
    }
  }
`

export const GET_ALL_LANDING_PAGES = gql`
  query ($filter: LandingPageFilter!) {
    landingPageCollection(order: pageMetaTitle_ASC, where: $filter) {
      items {
        path
        pageMetaTitle
        sys {
          publishedAt
        }
      }
      total
    }
  }
`

export const GET_CATEGORIES_LONG_FORM_CONTENT = gql`
  query ($filter: CategoriesLongFormContentFilter!) {
    categoriesLongFormContentCollection(where: $filter) {
      items {
        slug
        name
        description
        parentCategory {
          ... on CategoriesLongFormContent {
            slug
            name
            description
          }
        }
      }
    }
  }
`

export const GET_CATEGORIES_BY_PARENT_CATEGORY = gql`
  query ($filter: CategoriesLongFormContentFilter!) {
    categoriesLongFormContentCollection(where: $filter) {
      items {
        slug
        name
        description
        parentCategory {
          ... on CategoriesLongFormContent {
            slug
            name
            description
          }
        }
      }
    }
  }
`

export const GET_EXECUTIVE_BIO_BY_SLUG = gql`
  ${RICH_TEXT_EMBEDS_FRAGMENT}
  query GetExecutiveBioBySlug($filter: ExecutiveBioFilter!) {
    executiveBioCollection(where: $filter, limit: 1) {
      items {
        sys {
          id
          publishedAt
        }
        website
        name
        title
        slug
        pageMetaTitle
        metaDescription
        canonicalLinkOverride
        headshot {
          url
          fileName
          contentType
          title
          description
          width
          height
        }
        bodyCopy {
          ${RICH_TEXT_FIELD}
        }
        linksCollection(limit: 10) {
          items {
            ... on LInk {
              sys {
                id
              }
              fieldName
              text
              linkText {
                json
              }
              linkHref
              event
            }
          }
        }
        jsonLdSchemaCollection(limit: 5) {
          items {
            ... on JsonField {
              sys {
                id
              }
              name
              jsonContent
            }
          }
        }
      }
    }
  }
`

export const GET_ALL_EXECUTIVE_BIOS = gql`
  query GetAllExecutiveBios($filter: ExecutiveBioFilter!) {
    executiveBioCollection(where: $filter, order: name_ASC, limit: 200) {
      items {
        sys {
          publishedAt
        }
        slug
        name
        title
      }
      total
    }
  }
`
export const GET_LLMS_BY_SLUG = gql`
  ${RICH_TEXT_EMBEDS_FRAGMENT}
  query GetLlmsBySlug($filter: WwwLlmsFilter!) {
    wwwLlmsCollection(where: $filter, limit: 1) {
      items {
        sys {
          id
          publishedAt
        }
        website
        slug
        content {
          ${RICH_TEXT_FIELD}
        }
      }
    }
  }
`
